import {
  CleanupFunction,
  CompanyRoleIdWithName,
  DbotEvent,
  DbotEventDispatcher,
  DecisionItem,
  DecisionTextModel,
  DocumentContext,
  DocumentItem,
  isLanguageChanged,
  mapDecisionTextModel,
  mapDocumentLanguageToLocales,
  mapLocalesToDocumentLanguage,
  mapLocalizedDecisionTextsToModel,
  ProjectDocumentStatus,
  WithInitialization,
} from "@/dbot/common/commonDomain";
import { autorun, makeAutoObservable, reaction, runInAction } from "mobx";
import { identity } from "lodash";
import {
  isBoardMember,
  isChairperson,
  isDeputyBoardMember,
  isDeputyChairperson,
  isObserver,
  sortedRoles,
} from "@/utils/Roles";
import {
  BoardMeetingMinutes,
  BoardMemberOption,
  boardMemberOptionLabel,
  cannotSelectMeetingChairMessage,
  getDefaultSigners,
  getDefaultTwoSigners,
  getMeetingChair,
  incompetentBoardMembersFromJson,
  isSelectionOfSignersEnabled,
  lookupBoardMemberOption,
  SignersMode,
  sortBoardMemberOptions,
  toBoardMemberOption,
} from "./boardMeetingMinutesDomain";
import {
  BoardMeetingVoting,
  boardMeetingVoting,
  Vote,
  VotingMode,
  votingOutcomeForDecision,
} from "./boardMeetingVoting";
import { LocalizedText } from "@/common/common";
import {
  ExtraParticipant,
  getCompanyRoleLabelById,
  getSelectedOrDefault,
  getSortedBoard,
  handleRemoveCustomBoardMeetingPanel,
  mapRoleToProjectRole,
  prepProjectCompanyRoleModel,
  ProjectCompanyRole,
} from "@/dbot/common/commonBoardMeetingDomain";
import { filterDecisionRequiringVoting } from "@/dbot/freetext/freetextDomain";
import {
  downloadSignedDocumentAction,
  openSendForReviewDialogAction,
  openSendForSigningDialogAction,
} from "@/dbot/summary/summaryDomain";
import { BoardMeetingForm } from "@/dbot/noticeBoardMeeting/noticeBoardMeetingDomain";
import { newUuid } from "@/utils/uuid";
import { Language } from "@/i18n/languages";
import { TMessage } from "@/i18n/tt";
import { DecisionType } from "@/common/decisions";
import { DocumentType } from "@/common/documents";
import { MeetingPlace } from "@/meetings/meetingsDomain";
import {
  createEmailField,
  DocumentLanguageFieldModel,
  documentLanguageFieldModel,
  LocalDateFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms";
import { LocalizedTextEditorModel } from "@/text/localizedTextEditorDomain";
import { isExtensionPdf } from "@/utils/files";
import { RoleType } from "@/types/enums/RoleType.ts";
import { CompanyroleId } from "@/types/CompanyInfo.ts";

export class BoardMeetingMinutesForm implements WithInitialization {
  readonly type = DocumentType.board_meeting;
  readonly dispatch: DbotEventDispatcher;
  readonly context: DocumentContext<BoardMeetingMinutes>;

  selectedBoardMembers: OptionFieldModel<ProjectCompanyRole>;
  selectedCeos: OptionFieldModel<ProjectCompanyRole>;
  selectedDeputyMembers: OptionFieldModel<ProjectCompanyRole>;
  selectedObservers: OptionFieldModel<ProjectCompanyRole>;
  extraParticipants: ExtraParticipant[];
  extraParticipantFormModel?: { nameField: StringFieldModel; emailField: StringFieldModel };
  selectedSigningBoardMembers: OptionFieldModel<CompanyRoleIdWithName>;
  selectedSigningNotParticipatingBoardMembers: OptionFieldModel<ProjectCompanyRole>;
  selectedSigningCeos: OptionFieldModel<ProjectCompanyRole>;
  selectedSigningObservers: OptionFieldModel<ProjectCompanyRole>;
  extraSignatories: OptionFieldModel<ExtraParticipant>;
  meetingChair: RadioFieldModel<BoardMemberOption | undefined>;
  votesByDecision: Map<string, VotingModel>;
  deadlineForNotes: LocalDateFieldModel;
  numberOfBoardMembers: number;
  incompetentBoardMembers: Map<number, Set<string>>;
  documentLanguage: DocumentLanguageFieldModel;
  physicalMeetingSignersMode: RadioFieldModel<SignersMode>;
  decisionTextModels: DecisionTextModel[];
  board: ProjectCompanyRole[];
  additionalDetailsTextModel: LocalizedTextEditorModel;
  additionalParticipantDetailsTextModel: LocalizedTextEditorModel;
  additionalDetailsTexts: LocalizedText[]; // initial value
  additionalParticipantDetailsTexts: LocalizedText[];

  constructor(context: DocumentContext<BoardMeetingMinutes>, dispatch: DbotEventDispatcher) {
    const data = context.documentData;
    this.context = context;
    this.dispatch = dispatch;
    makeAutoObservable(this, {}, { autoBind: true });
    this.board = getSortedBoard(context);
    this.numberOfBoardMembers = data.numberOfBoardMembers;

    const sortedBoardMemberOptions = sortBoardMemberOptions(
      context.initialState.roles.map(toBoardMemberOption),
    );

    this.selectedBoardMembers = prepProjectCompanyRoleModel({
      options: this.boardMembers,
      selectedIds: data.participants,
      defaultValue: this.boardMembers,
    });

    this.selectedCeos = prepProjectCompanyRoleModel({
      options: this.availableCeos,
      selectedIds: data.participants,
      defaultValue: this.availableCeos,
    });

    this.selectedDeputyMembers = prepProjectCompanyRoleModel({
      options: this.deputyBoardMembers,
      selectedIds: data.participants,
      defaultValue: [],
    });

    this.selectedObservers = prepProjectCompanyRoleModel({
      options: this.observers,
      selectedIds: data.participants,
      defaultValue: [],
    });

    this.extraParticipants = data?.extraParticipants ?? [];

    this.meetingChair = new RadioFieldModel({
      value: data.meetingChair
        ? lookupBoardMemberOption(sortedBoardMemberOptions, data.meetingChair)
        : undefined,
      options: () =>
        sortBoardMemberOptions([
          ...this.selectedBoardMembers.selected,
          ...this.selectedCeos.selected,
          ...this.selectedDeputyMembers.selected,
        ]),
      idFn: (v) => v?.companyroleId.toString() ?? "",
      labelFn: (v) => (v ? boardMemberOptionLabel(v) : { translated: "" }),
    });

    this.deadlineForNotes = new LocalDateFieldModel({ value: data.deadlineForNotes });
    this.incompetentBoardMembers = incompetentBoardMembersFromJson(data.incompetentBoardMembers);

    this.votesByDecision = new Map(
      this.context.currentDecisions.map((d) => [
        d.decisionId,
        this.initVotingModel(boardMeetingVoting(d.decisionId, d.type === DecisionType.freetext)),
      ]),
    );

    if (data.boardMeetingVotes) {
      for (const v of data.boardMeetingVotes) {
        this.votesByDecision.set(v.decisionId, this.initVotingModel(v));
      }
    }

    this.documentLanguage = documentLanguageFieldModel({
      orgNo: context.orgNo,
      value: mapLocalesToDocumentLanguage(data.documentLanguage),
    });

    this.physicalMeetingSignersMode = new RadioFieldModel<SignersMode>({
      value: data.physicalMeetingSignersMode,
      options: Object.values(SignersMode),
      idFn: identity,
      labelFn: (v) => {
        switch (v) {
          case SignersMode.ALL:
            return {
              key: "documents.boardMeeting.eachWillSign",
              params: { n: this.presentBoardMembers.length },
            };
          case SignersMode.SELECTED:
            return { key: "documents.boardMeeting.twoPersonsChosenForSigning" };
        }
      },
    });

    this.selectedSigningBoardMembers = new OptionFieldModel({
      value: data.selectedSigningBoardMembers ?? this.getDefaultSigners(),
      options: () => this.presentBoardMembers,
      idFn: (v) => v.companyroleId.toString(),
      labelFn: (v) => getCompanyRoleLabelById(v.companyroleId, this.roles),
      validator: (selected) => {
        if (
          this.physicalMeetingSignersMode.value === SignersMode.SELECTED &&
          selected.length !== 2
        ) {
          return {
            key: "documents.boardMeeting.signatories.error.selectExactlyTwo",
          };
        }
      },
    });

    this.selectedSigningNotParticipatingBoardMembers = prepProjectCompanyRoleModel({
      options: () => this.selectedBoardMembers.unselected,
      selectedIds: data.optionalSignatories,
      defaultValue: [],
    });
    this.selectedSigningCeos = prepProjectCompanyRoleModel({
      options: () => this.selectedCeos.selected,
      selectedIds: data.optionalSignatories,
      defaultValue: [],
    });
    this.selectedSigningObservers = prepProjectCompanyRoleModel({
      options: () => this.selectedObservers.selected,
      selectedIds: data.optionalSignatories,
      defaultValue: [],
    });

    this.extraSignatories = new OptionFieldModel({
      value: data.extraSignatories,
      options: () => this.extraParticipants,
      idFn: (v) => v.id,
      labelFn: (v) => ({ translated: v.name }),
    });

    this.updateMeetingChair();

    this.additionalDetailsTexts = data?.additionalDetailsTexts ?? [];
    this.additionalParticipantDetailsTexts = data.additionalParticipantDetailsTexts ?? [];

    this.additionalDetailsTextModel = new LocalizedTextEditorModel({
      texts: data?.additionalDetailsTexts,
      isReadOnly: () => context.isLocked,
      label: { key: "documents.minutes.additionalDetails" },
      aiClient: context.aiClient,
    });

    this.additionalParticipantDetailsTextModel = new LocalizedTextEditorModel({
      texts: data?.additionalParticipantDetailsTexts,
      isReadOnly: () => context.isLocked,
      label: { key: "documents.minutes.additionalDetails" },
      aiClient: context.aiClient,
    });

    this.decisionTextModels = mapLocalizedDecisionTextsToModel(
      context,
      dispatch,
      DocumentType.board_meeting,
      this.documentLanguage.value,
    );
  }

  get showOptionalSignatories() {
    return (
      [
        ...this.selectedSigningNotParticipatingBoardMembers.options,
        ...this.selectedSigningCeos.options,
        ...this.selectedSigningObservers.options,
        ...this.extraSignatories.options,
      ].length > 0
    );
  }

  get decisionMatters() {
    return filterDecisionRequiringVoting(this.context.currentDecisions, DocumentType.board_meeting);
  }

  init(): CleanupFunction {
    const cleanupFunctions = [
      autorun(() => this.handleLanguageChanges()),
      reaction(
        () => this.isSelectionOfSignersEnabled,
        (enabled) => {
          if (!enabled) {
            this.setPhysicalMeetingSignersMode(SignersMode.ALL);
          }
        },
      ),
    ];

    return () => {
      cleanupFunctions.forEach((f) => f());
    };
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/decision-added":
        this.initVotesForDecision(event.decisionId, event.isFreetext);
        this.initDecisionTextModel(event.decisionId);
        break;
      case "dbot/decision-removed":
        this.removeVotesForDecision(event.decisionId);
        break;
      case "dbot/update-board-meeting-participants":
        this.updateParticipants(event.participants, event.extraParticipants);
        break;
      case "dbot/remove-custom-panel":
        handleRemoveCustomBoardMeetingPanel(this.context, event);
        break;
    }
  }

  updateParticipants(participants: CompanyroleId[], extraParticipants: ExtraParticipant[]) {
    this.selectedBoardMembers.setSelected(
      getSelectedOrDefault({ options: this.boardMembers, selectedIds: participants }) ?? [],
    );
    this.selectedCeos.setSelected(
      getSelectedOrDefault({ options: this.availableCeos, selectedIds: participants }) ?? [],
    );
    this.selectedDeputyMembers.setSelected(
      getSelectedOrDefault({ options: this.deputyBoardMembers, selectedIds: participants }) ?? [],
    );
    this.selectedObservers.setSelected(
      getSelectedOrDefault({ options: this.observers, selectedIds: participants }) ?? [],
    );
    this.extraParticipants = extraParticipants;
    this.resetSelectedSigners();
    this.updateMeetingChair();
  }

  get roles(): ProjectCompanyRole[] {
    return sortedRoles(this.context.initialState.roles).map(mapRoleToProjectRole);
  }

  get boardMeetingForm(): RadioFieldModel<BoardMeetingForm> {
    return this.context.boardMeetingFields.boardMeetingForm;
  }

  get meetingPlace(): RadioFieldModel<MeetingPlace> {
    return this.context.boardMeetingFields.meetingPlace;
  }

  get inlineAddress(): StringFieldModel {
    return this.context.boardMeetingFields.inlineAddress;
  }

  get showAddress(): boolean {
    return (
      this.boardMeetingForm.value === BoardMeetingForm.PHYSICAL ||
      this.boardMeetingForm.value === BoardMeetingForm.HYBRID
    );
  }

  get showInlineAddress(): boolean {
    return this.showAddress && this.meetingPlace.value === MeetingPlace.inlineAddress;
  }

  get boardMeetingNumber(): StringFieldModel {
    return this.context.boardMeetingFields.boardMeetingNumber;
  }

  get meetingStartDate() {
    return this.context.boardMeetingFields.meetingStartDate;
  }

  get meetingStartTime() {
    return this.context.boardMeetingFields.meetingStartTime;
  }

  get meetingEndTime() {
    return this.context.boardMeetingFields.meetingEndTime;
  }

  get documentId(): string {
    return this.context.documentId;
  }

  get documentItem(): DocumentItem {
    return this.context.currentDocuments.find((d) => d.documentId === this.documentId)!;
  }

  get documentStatus(): ProjectDocumentStatus | undefined {
    return this.context.documentStatuses.find((s) => s.documentId === this.documentId);
  }

  get mapRecipients(): CompanyRoleIdWithName[] {
    return [
      ...this.selectedBoardMembers.selected,
      ...this.selectedCeos.selected,
      ...this.selectedDeputyMembers.selected,
    ].map((member) => ({
      name: member.name,
      companyroleId: member.companyroleId,
    }));
  }

  addExtraParticipant() {
    this.extraParticipantFormModel = {
      nameField: new StringFieldModel({ required: true }),
      emailField: createEmailField({ required: true }),
    };
  }

  cancelAddExtraParticipant() {
    this.extraParticipantFormModel = undefined;
  }

  submitExtraParticipant() {
    if (!this.extraParticipantFormModel) {
      return;
    }
    const { nameField, emailField } = this.extraParticipantFormModel;
    nameField.validate();
    emailField.validate();
    if (nameField.isValid && emailField.isValid) {
      this.extraParticipants.push({
        id: newUuid(),
        name: nameField.validatedValue,
        email: emailField.validatedValue!,
      });
      nameField.clear();
      emailField.clear();
      nameField.focus();
    }
  }

  removeExtraParticipant(participant: ExtraParticipant) {
    this.extraParticipants.splice(this.extraParticipants.indexOf(participant), 1);
  }

  get sendForReviewAction() {
    return openSendForReviewDialogAction(
      {
        document: this.documentItem,
        signingStatus: this.documentStatus?.signingStatus,
        recipients: this.mapRecipients,
      },
      this.context,
    );
  }

  get decisionAttachments() {
    return this.currentDecisions
      .flatMap((a) => a.attachments)
      .filter((a) => a.selectedDocuments.includes(DocumentType.board_meeting));
  }

  get includesUnsupportedDocumentAttachments() {
    return (
      this.context.attachments.length > 0 &&
      this.context.attachments.some((a) => !isExtensionPdf(a.filename))
    );
  }

  get includesUnsupportedDecisionAttachments() {
    return (
      this.decisionAttachments.length > 0 &&
      this.decisionAttachments.some((a) => !isExtensionPdf(a.filename))
    );
  }

  get openSendForSigningDialogAction() {
    return openSendForSigningDialogAction({
      document: this.documentItem,
      signingStatus: this.documentStatus?.signingStatus,
      showUnsupportedAttachmentWarning:
        this.includesUnsupportedDocumentAttachments || this.includesUnsupportedDecisionAttachments,
      attachments: this.context.attachments,
    });
  }

  get downloadSignedDocumentAction() {
    return downloadSignedDocumentAction({
      document: this.documentItem,
      signingStatus: this.documentStatus?.signingStatus,
      numberOfSignedDocuments: this.documentStatus?.numberOfSignedDocuments,
    });
  }

  get currentDecisions(): DecisionItem[] {
    return this.context.currentDecisions;
  }

  get canSelectMeetingChair(): boolean {
    return !this.isChairpersonPresent && !this.isDeputyChairpersonPresent;
  }

  get cannotSelectMeetingChairMessage(): TMessage | undefined {
    return cannotSelectMeetingChairMessage(this);
  }

  get isNorwegianRequiredForMinutesWithPoA(): boolean {
    const isDocumentOnlyEnglish = this.documentLanguage.value === "en";
    const isDecisionIncreaseOfShareCapital = this.currentDecisions.some(
      (d) =>
        d.decisionData?.type === DecisionType.increaseOfShareCapital &&
        d.decisionData?.usePowerOfAttorney === true,
    );
    return isDecisionIncreaseOfShareCapital && isDocumentOnlyEnglish;
  }

  setMeetingChair(option: BoardMemberOption | undefined) {
    this.meetingChair.setValue(option);
    this.resetSelectedSigners();
  }

  toggleAllBoardMembers(checked: boolean) {
    if (checked) {
      this.selectedBoardMembers.selectAll();
    } else {
      this.selectedBoardMembers.clear();
    }
    this.resetSelectedSigners();
    this.updateMeetingChair();
  }

  toggleAllDeputyBoardMembers(checked: boolean) {
    if (checked) {
      this.selectedDeputyMembers.selectAll();
    } else {
      this.selectedDeputyMembers.clear();
    }
    this.resetSelectedSigners();
    this.updateMeetingChair();
  }

  toggleSelectedBoardMember(option: ProjectCompanyRole) {
    this.selectedBoardMembers.toggle(option);
    this.resetSelectedSigners();
    this.updateMeetingChair();
  }

  toggleSelectedDeputyBoardMember(option: ProjectCompanyRole) {
    this.selectedDeputyMembers.toggle(option);
    this.resetSelectedSigners();
    this.updateMeetingChair();
  }

  toggleIncompetentBoardMember(companyroleId: number, checked: boolean) {
    if (!checked) {
      this.incompetentBoardMembers.delete(companyroleId);
    } else if (!this.incompetentBoardMembers.has(companyroleId)) {
      this.incompetentBoardMembers.set(companyroleId, new Set());
    }
  }

  isIncompetent(companyroleId: number): boolean {
    return this.incompetentBoardMembers.has(companyroleId);
  }

  toggleDecisionForIncompetentMember(companyroleId: number, decisionId: string, checked: boolean) {
    if (checked) {
      this.incompetentBoardMembers.get(companyroleId)?.add(decisionId);
    } else {
      this.incompetentBoardMembers.get(companyroleId)?.delete(decisionId);
    }
  }

  isIncompetentForDecision(companyroleId: number, decisionId: string): boolean {
    return this.incompetentBoardMembers.get(companyroleId)?.has(decisionId) ?? false;
  }

  updateMeetingChair() {
    this.meetingChair.setValue(
      getMeetingChair({
        currentChair: this.meetingChair.value,
        presentMembers: this.presentBoardMembers,
        board: this.boardMembers,
      }),
    );
  }

  get chairperson(): ProjectCompanyRole | undefined {
    return this.roles.find(isChairperson);
  }

  get deputyChairperson(): ProjectCompanyRole | undefined {
    return this.roles.find(isDeputyChairperson);
  }

  get availableCeos() {
    return this.roles.filter((r) => r.role === RoleType.CEO);
  }

  get boardMembers(): ProjectCompanyRole[] {
    return this.roles.filter(isBoardMember);
  }

  get deputyBoardMembers(): ProjectCompanyRole[] {
    return this.roles.filter(isDeputyBoardMember);
  }

  get observers(): ProjectCompanyRole[] {
    return this.roles.filter(isObserver);
  }

  get presentBoardMembers(): CompanyRoleIdWithName[] {
    return [...this.selectedBoardMembers.selected, ...this.selectedDeputyMembers.selected];
  }

  get isChairpersonPresent() {
    return this.presentBoardMembers.some(
      (member) => this.chairperson?.companyroleId === member.companyroleId,
    );
  }

  get isDeputyChairpersonPresent() {
    return this.presentBoardMembers.some(
      (member) => this.deputyChairperson?.companyroleId === member.companyroleId,
    );
  }

  get isLocked(): boolean {
    return this.context.isLocked;
  }

  get locale(): Language {
    return this.context.locale;
  }

  get isSelectionOfSignersEnabled() {
    return isSelectionOfSignersEnabled(this.boardMeetingForm.value);
  }

  isUnanimous(decisionId: string): boolean {
    return this.votesByDecision.get(decisionId)?.isUnanimous ?? false;
  }

  toggleDecisionUnanimous(decisionId: string) {
    const voting = this.votesByDecision.get(decisionId);
    if (voting) {
      voting.isUnanimous = !voting.isUnanimous;
    }
  }

  eligibleSelectableBoardMembersForVoting(decisionId: string): Array<CompanyRoleIdWithName> {
    return [...this.selectedBoardMembers.selected, ...this.selectedDeputyMembers.selected].filter(
      (member) => !this.isIncompetentForDecision(member.companyroleId, decisionId),
    );
  }

  toggleVotedAgainst(member: CompanyRoleIdWithName, decisionId: string) {
    const voting = this.votesByDecision.get(decisionId);
    if (!voting) {
      return;
    }
    voting.votedAgainst.toggle(member);
  }

  setPhysicalMeetingSignersMode(option: SignersMode) {
    this.physicalMeetingSignersMode.setValue(option);
    this.resetSelectedSigners();
  }

  resetSelectedSigners() {
    this.selectedSigningBoardMembers.setSelected(this.getDefaultSigners());
    this.selectedSigningBoardMembers.validate();
  }

  getDefaultSigners() {
    return getDefaultSigners(
      this.presentBoardMembers,
      getDefaultTwoSigners(
        this.presentBoardMembers,
        this.chairperson,
        this.deputyChairperson,
        this.meetingChair?.value,
      ),
      this.boardMeetingForm.value,
      this.physicalMeetingSignersMode.value,
    );
  }

  initVotingModel(data: BoardMeetingVoting): VotingModel {
    return new VotingModel({
      data,
      participatingMembers: () => this.eligibleSelectableBoardMembersForVoting(data.decisionId),
    });
  }

  initDecisionTextModel(decisionId: string) {
    const decisionTextModel = this.decisionTextModels.find((d) => d.decisionId === decisionId);
    if (decisionTextModel === undefined) {
      const decision = this.context.currentDecisions.find((d) => d.decisionId === decisionId);
      const documentLanguages = mapDocumentLanguageToLocales(this.documentLanguage.value);
      if (decision) {
        const model = mapDecisionTextModel(
          this.dispatch,
          decision,
          DocumentType.board_meeting,
          this.context,
          documentLanguages,
        );
        this.decisionTextModels.push(model);
      }
    }
  }

  initVotesForDecision(decisionId: string, isFreetext: boolean) {
    if (!this.votesByDecision.has(decisionId)) {
      this.votesByDecision.set(
        decisionId,
        this.initVotingModel(boardMeetingVoting(decisionId, isFreetext)),
      );
    }
  }

  removeVotesForDecision(decisionId: string) {
    this.votesByDecision.delete(decisionId);
  }

  votingModeModelForDecision(decisionId: string): RadioFieldModel<VotingMode> {
    return this.votesByDecision.get(decisionId)!.votingModeModel;
  }

  isVotedAgainst(decision: DecisionItem): boolean {
    return this.votingModeModelForDecision(decision.decisionId).value === VotingMode.VOTED_AGAINST;
  }

  isVotedUsingReview(decision: DecisionItem): boolean {
    return this.votingModeModelForDecision(decision.decisionId).value === VotingMode.VOTING;
  }

  votingListUsingReview(decision: DecisionItem): BoardMeetingDecisionVoter[] {
    const votes = this.context.boardMeetingVotes.filter(
      (v) => v.decisionId === decision.decisionId,
    );
    const participants = this.eligibleSelectableBoardMembersForVoting(decision.decisionId);
    return participants.map<BoardMeetingDecisionVoter>((participant) => {
      const voter = votes.find((v) => v.companyroleId === participant.companyroleId);
      return {
        name: participant.name,
        companyroleId: participant.companyroleId,
        vote: voter?.vote,
        votedAt: voter?.votedAt,
      };
    });
  }

  unapprovedVotingOutcomeForDecision(
    decisionId: string,
    votesUsingVoting: BoardMeetingDecisionVoter[],
  ): TMessage | undefined {
    const outcome = votingOutcomeForDecision(
      {
        presentBoardMembers: this.presentBoardMembers,
        incompetentBoardMemberIds: incompetentBoardMemberIds(
          decisionId,
          this.incompetentBoardMembers,
        ),
        meetingChair: this.meetingChair?.value,
        numberOfBoardMembers: this.boardMembers.length,
      },
      this.votesByDecision.get(decisionId)?.toJSON(),
      votesUsingVoting,
    );

    if (outcome.type === "approved") {
      return undefined;
    }
    switch (outcome.reason.type) {
      case "majorityVotedAgainst":
        return {
          key: "dbot.boardmeeting.votingOutcomeReason.majorityVotedAgainst",
          params: outcome.reason,
        };
      case "meetingChairCastingVote":
        return {
          key: "dbot.boardmeeting.votingOutcomeReason.meetingChairCastingVote",
          params: outcome.reason,
        };
      case "moreThanOneThirdMustVoteFor":
        return {
          key: "dbot.boardmeeting.votingOutcomeReason.moreThanOneThirdMustVoteFor",
          params: outcome.reason,
        };
      case "randomChoice":
        return {
          key: "dbot.boardmeeting.votingOutcomeReason.randomChoice",
          params: outcome.reason,
        };
      case "boardQuorumNotAchieved":
        return {
          key: "dbot.boardmeeting.votingOutcomeReason.boardQuorumNotAchieved",
          params: outcome.reason,
        };
      case "boardQuorumNotAchievedForDecision":
        return {
          key: "dbot.boardmeeting.votingOutcomeReason.boardQuorumNotAchievedForDecision",
          params: outcome.reason,
        };
    }
  }

  isDecisionMatter(decisionId: string): boolean {
    return this.decisionMatters.includes(decisionId);
  }

  private handleLanguageChanges() {
    const documentLanguages = mapDocumentLanguageToLocales(this.documentLanguage.value);
    if (
      isLanguageChanged(documentLanguages, this.additionalDetailsTextModel.textModels) ||
      isLanguageChanged(documentLanguages, this.additionalParticipantDetailsTextModel.textModels)
    ) {
      runInAction(() => {
        this.additionalDetailsTextModel.onLanguageChange(
          documentLanguages,
          this.additionalDetailsTexts,
        );
        this.additionalParticipantDetailsTextModel.onLanguageChange(
          documentLanguages,
          this.additionalParticipantDetailsTexts,
        );
        this.decisionTextModels = mapLocalizedDecisionTextsToModel(
          this.context,
          this.dispatch,
          DocumentType.board_meeting,
          this.documentLanguage.value,
        );
      });
    }
  }

  get isValid(): boolean {
    return this.selectedSigningBoardMembers.isValid;
  }

  validate(): boolean {
    this.selectedSigningBoardMembers.validate();
    return this.isValid;
  }
}

export type BoardMeetingDecisionVoter = {
  companyroleId: number;
  name: string;
  vote?: Vote;
  votedAt?: string;
};

export function incompetentBoardMemberIds(
  decisionId: string,
  mapping: Map<number, Set<string>>,
): number[] {
  return Array.from(mapping.entries()).reduce<number[]>((acc, [companyroleId, decisions]) => {
    if (decisions.has(decisionId)) {
      acc.push(companyroleId);
    }
    return acc;
  }, []);
}

export class VotingModel {
  mode?: VotingMode;
  isUnanimous: boolean;
  isApproved: boolean;
  votedAgainst: OptionFieldModel<CompanyRoleIdWithName>;
  votingModeModel: RadioFieldModel<VotingMode>;

  constructor(
    public params: {
      data: BoardMeetingVoting;
      participatingMembers: () => CompanyRoleIdWithName[];
    },
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.mode = params.data.mode;
    this.isUnanimous = this.mode === VotingMode.UNANIMOUS || params.data.isUnanimous;
    this.isApproved = params.data.isApproved;
    this.votedAgainst = new OptionFieldModel<CompanyRoleIdWithName>({
      value: params.data.membersVotedAgainst,
      options: params.participatingMembers,
      idFn: (r) => r.companyroleId.toString(),
      labelFn: (r) => ({ translated: r.name }),
    });
    this.votingModeModel = new RadioFieldModel<VotingMode>({
      value: params.data?.mode ?? VotingMode.UNANIMOUS,
      options: Object.values(VotingMode),
      idFn: identity,
      labelFn: (v) => {
        switch (v) {
          case VotingMode.UNANIMOUS:
            return { key: "decisions.votes.isUnanimous" };
          case VotingMode.VOTED_AGAINST:
            return { key: "documents.boardMeeting.chooseWhoVotedAgainst" };
          case VotingMode.VOTING:
            return { key: "documents.boardMeeting.voting" };
        }
      },
    });
  }

  get decisionId(): string {
    return this.params.data.decisionId;
  }

  get hasParticipatingMembers(): boolean {
    return this.votedAgainst.options.length > 0;
  }

  toJSON(): BoardMeetingVoting {
    return {
      decisionId: this.decisionId,
      isUnanimous: this.isUnanimous,
      isApproved: this.isApproved,
      membersVotedAgainst: this.votedAgainst.selected,
      mode: this.votingModeModel.value,
    };
  }
}
